/**
 * Hook de autenticação unificado que combina dados do Keycloak com o Supabase.
 *
 * Este hook é parte da estratégia de migração gradual do sistema de permissões:
 * - Autenticação: Keycloak (OAuth2)
 * - Dados do usuário: API Keycloak (role, generator da nova engine)
 * - Compatibilidade: Lookup no Supabase para obter generator_id legacy (UUID)
 *
 * Estrutura de IDs:
 * - generator_id (number): ID da nova engine (vem do Keycloak, = id_new_engine_generators)
 * - generator_id_legacy (string UUID): ID do Supabase (para queries RLS existentes)
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile, UserProfile } from "./useProfile";
import { useCallback, useMemo } from "react";
import {
  getStoredAccessToken,
  getGeneratorsFromToken,
  getPermissionsFromToken,
  isMigratedToProfiles,
} from "@/utils/keycloakRoles";
import type { Permission } from "@/constants/permissions";

export interface UnifiedUserProfile extends UserProfile {
  /**
   * ID do generator no Supabase (UUID) - para compatibilidade com queries existentes.
   * Obtido via lookup: generators.generator_id WHERE id_new_engine_generators = profile.generator_id
   */
  generator_id_legacy: string | null;

  /**
   * Nome do generator (para exibição)
   */
  generator_name: string | null;
}

interface UnifiedAuthState {
  profile: UnifiedUserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  keycloakToken: string | null;
}

/**
 * Hook que unifica autenticação Keycloak com dados de compatibilidade do Supabase.
 *
 * Uso:
 * ```tsx
 * const { profile, isLoading, isAuthenticated } = useUnifiedAuth();
 *
 * // Usar generator_id (nova engine) para novas queries
 * const newEngineId = profile?.generator_id;
 *
 * // Usar generator_id_legacy (UUID) para queries RLS existentes
 * const legacyId = profile?.generator_id_legacy;
 * ```
 */
export const useUnifiedAuth = (): UnifiedAuthState => {
  // 1. Obter dados do Keycloak via useProfile
  const {
    profile: keycloakProfile,
    isLoading: isProfileLoading,
    error: profileError,
  } = useProfile();

  // 2. Lookup do generator_id legacy no Supabase (se tiver generator_id da nova engine)
  const {
    data: generatorData,
    isLoading: isGeneratorLoading,
    error: generatorError,
  } = useQuery({
    queryKey: ["generator-legacy-lookup", keycloakProfile?.generator_id],
    queryFn: async () => {
      if (!keycloakProfile?.generator_id) {
        return null;
      }

      // Buscar o generator_id (UUID) correspondente ao id_new_engine_generators
      // Nota: O campo id_new_engine_generators foi adicionado à tabela generators
      // para mapear entre IDs da nova engine e IDs do Supabase
      const { data, error } = await supabase
        .from("generators")
        .select("generator_id, generator_name")
        .eq("id_new_engine_generators", keycloakProfile.generator_id)
        .maybeSingle();

      if (error) {
        console.warn(
          "⚠️ useUnifiedAuth: Erro ao buscar generator legacy:",
          error.message,
        );
        // Não lançar erro - retornar null e continuar sem o ID legacy
        return null;
      }

      return data;
    },
    enabled: !!keycloakProfile?.generator_id,
    staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    retry: 1,
  });

  // 3. Combinar dados em um perfil unificado
  const unifiedProfile: UnifiedUserProfile | null = keycloakProfile
    ? {
        ...keycloakProfile,
        generator_id_legacy: generatorData?.generator_id ?? null,
        generator_name: generatorData?.generator_name ?? null,
      }
    : null;

  // Calcular estados
  const isLoading =
    isProfileLoading || (!!keycloakProfile?.generator_id && isGeneratorLoading);
  const isAuthenticated = !!keycloakProfile && !isProfileLoading;
  const error = profileError || generatorError || null;

  // Obter token do Keycloak para edge functions
  const keycloakToken = getStoredAccessToken();

  return {
    profile: unifiedProfile,
    isLoading,
    isAuthenticated,
    error: error as Error | null,
    keycloakToken,
  };
};

/**
 * Hook simplificado para obter apenas o generator_id compatível com o sistema atual.
 *
 * Retorna o generator_id_legacy (UUID) se disponível, caso contrário retorna null.
 * Use este hook quando precisar do ID no formato antigo para queries existentes.
 */
export const useGeneratorIdLegacy = (): {
  generatorId: string | null;
  isLoading: boolean;
} => {
  const { profile, isLoading } = useUnifiedAuth();

  return {
    generatorId: profile?.generator_id_legacy ?? null,
    isLoading,
  };
};

/**
 * Hook para verificar permissões baseado no perfil unificado.
 * Mantém compatibilidade com o sistema de permissões existente.
 */
export const usePermissions = () => {
  const { profile, isLoading, isAuthenticated } = useUnifiedAuth();

  // O token é a fonte: as permissões chegam nele em
  // `resource_access["portal-gerador"].roles` assim que o usuário abre o site,
  // sem chamada de rede. Decodificar é caro para fazer a cada render, então o
  // memo é chaveado no próprio token — refresh troca a string e recalcula.
  const token = getStoredAccessToken();
  const permissions = useMemo(() => (token ? getPermissionsFromToken() : []), [token]);
  const granted = useMemo(() => new Set(permissions), [permissions]);
  const isMigrated = useMemo(() => (token ? isMigratedToProfiles() : false), [token]);

  const isInternal = profile?.is_internal ?? false;

  /**
   * Interno tem acesso total, independente de role (GR-561, regra 10).
   *
   * Não há indulgência para quem ainda não foi migrado: aqui a resposta é
   * honesta. Quem for ligar bloqueio na Onda 1 precisa consultar `isMigrated`
   * antes de negar — do contrário derruba todo usuário que a migração ainda
   * não alcançou (GR-561, regra 16).
   */
  const hasPermission = useCallback(
    (permission: Permission | string): boolean =>
      isInternal || granted.has(permission),
    [isInternal, granted],
  );

  /**
   * Para permissões do ACESSO BASE — as do grupo `default`, que todo usuário
   * de gerador já tinha antes do GR-637.
   *
   * Além do interno, deixa passar quem AINDA NÃO FOI MIGRADO (GR-561, regra
   * 16): o token dele não tem client role nenhuma, então `hasPermission`
   * responderia `false` e tiraria um acesso que ele sempre teve. É a mesma
   * escapatória que `PermissionRoute` aplica nas rotas, aqui disponível para
   * controle dentro de tela (aba, botão de download).
   *
   * NÃO usar para acesso ADITIVO (evidências, add-on, financeiro): ali quem
   * não tem, nunca teve, e perdoar o não migrado alargaria o acesso em vez de
   * proteger. Esse caso usa `hasPermission`/`hasAnyPermission`.
   */
  const hasBaselinePermission = useCallback(
    (permission: Permission | string): boolean =>
      isInternal || !isMigrated || granted.has(permission),
    [isInternal, isMigrated, granted],
  );

  const hasAnyPermission = useCallback(
    (...list: Array<Permission | string>): boolean =>
      isInternal || list.some((p) => granted.has(p)),
    [isInternal, granted],
  );

  const hasAllPermissions = useCallback(
    (...list: Array<Permission | string>): boolean =>
      isInternal || list.every((p) => granted.has(p)),
    [isInternal, granted],
  );

  return {
    // Permissões granulares do catálogo GR-637. Só leitura por enquanto —
    // nenhuma tela bloqueia por elas ainda.
    permissions,
    hasPermission,
    hasBaselinePermission,
    hasAnyPermission,
    hasAllPermissions,
    isMigrated,

    isLoading,
    isAuthenticated,

    // Roles
    isInternal,
    isAdmin: profile?.permission_role === "admin" || profile?.is_internal,
    isStandard: profile?.permission_role === "standard",
    role: profile?.permission_role ?? null,

    // Generator IDs (ambos formatos disponíveis)
    generatorId: profile?.generator_id ?? null, // Nova engine (number)
    generatorIdLegacy: profile?.generator_id_legacy ?? null, // Supabase (UUID string)
    generatorName: profile?.generator_name ?? null,

    // Helpers de permissão
    // A terceira perna (`generators[].role`) é o caminho LEGADO, mantido em
    // paralelo (GR-561, [BREAKING]): garante o acesso de quem ainda não foi
    // migrado para os grupos e o rollback do Portal. O caminho novo entra por
    // `permission_role`, que `getTokenBasedUserType` deriva do claim
    // `groups`.
    canManageUsers:
      profile?.is_internal === true ||
      profile?.permission_role === "admin" ||
      getGeneratorsFromToken().some((g) => g.role === "admin"),
    canViewAllGenerators: profile?.is_internal ?? false,
    canEditOwnProfile: isAuthenticated,
  };
};

export default useUnifiedAuth;
